import Form, { FormApi } from '@atlaskit/form';
import { css } from '@emotion/react';
import { token } from '@atlaskit/tokens';
import { Button, ButtonAppearance } from '../Button';
import { BREAKPOINTS } from '@constants/breakpoints';
import { buttonWrapper, NewsletterInput } from './NewsletterInput';

export const PRIVACY_POLICY_HREF = 'https://www.atlassian.com/legal/privacy-policy#what-this-policy-covers';
export const PRIVACY_POLICY =
  'By signing up, you agree to receive promotional emails including product news, events, and more from Atlassian and its affiliates, and acknowledge and accept the ';

export type FormInputs = {
  email: string;
};

interface NewsletterSignupProps {
  response: {
    message?: string;
    isError: boolean;
  };
  onSubmit: (data: FormInputs) => Promise<void>;
  onPrivacyPolicyClick?: () => void | Promise<void>;
}

export const NewsletterSignupForm: React.FC<NewsletterSignupProps> = ({ response, onSubmit, onPrivacyPolicyClick }) => {
  const handleSubmit = async (data: FormInputs, form: FormApi<FormInputs>) => {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    const errors = {
      email: !emailPattern.test(data.email) ? 'Enter a valid email address.' : undefined,
    };

    if (!errors.email) {
      await onSubmit(data);
      form.reset();
    }

    return errors;
  };

  return (
    <Form<FormInputs> onSubmit={handleSubmit}>
      {({ formProps, submitting }) => (
        <form {...formProps} css={formWrapperStyles} data-testid="signup-form" noValidate>
          <div css={formActionStyles}>
            <NewsletterInput response={response} isLoading={submitting} />
            <div css={buttonWrapper({ isMobile: true })}>
              <Button
                label="Sign up"
                type="submit"
                appearance={ButtonAppearance.PRIMARY}
                isLoading={submitting}
                fullWidth
              />
            </div>
          </div>
          <p css={acknowledgeStyle}>
            {PRIVACY_POLICY}
            <a href={PRIVACY_POLICY_HREF} target="_blank" onClick={onPrivacyPolicyClick}>
              Atlassian Privacy Policy
            </a>
            .
          </p>
        </form>
      )}
    </Form>
  );
};

const formWrapperStyles = css({
  width: '100%',
  paddingLeft: token('space.800'),
  paddingRight: token('space.800'),
  [BREAKPOINTS.MOBILE.MEDIA_QUERY]: {
    padding: 0,
  },
  [BREAKPOINTS.TABLET.MEDIA_QUERY]: {
    marginTop: token('space.300'),
  },
  [BREAKPOINTS.DESKTOP.MEDIA_QUERY]: {
    marginTop: token('space.400'),
  },
});

const formActionStyles = css({
  [BREAKPOINTS.MOBILE.MEDIA_QUERY]: {
    flexDirection: 'column',
    gap: token('space.200'),
  },
});

const acknowledgeStyle = css({
  fontFamily: 'Inter, sans-serif',
  maxWidth: '405px',
  color: token('color.text.subtlest'),
  fontWeight: '400',
  fontSize: '12px',
  marginTop: token('space.100'),
  '& a': {
    color: token('color.text.selected'),
  },
  [BREAKPOINTS.MOBILE.MEDIA_QUERY]: { maxWidth: 'unset' },
});
